// File: src/api/payment.service.js
// --------------------------------
//
// Centralised wrapper for **/api/XtramBillingSubscriptions** endpoints.
// Every call automatically attaches the current JWT.

import config from '../config';
import selectedEnvironment from '../env';

const BASEAPI = config[selectedEnvironment].API_URL;
const ROOT = `${BASEAPI}/XtramBillingSubscriptions`;
const EXPORT_ROOT = `${BASEAPI}/XtramBillingExport`;
const ARRANGEMENT_ROOT = `${BASEAPI}/XtramBillingAdminArrangements`;
const ACC_CALC_ROOT = `${BASEAPI}/XtramBillingAccountantArrangementsCalculation`;
const CLI_CALC_ROOT = `${BASEAPI}/XtramBillingClientsArrangementsCalculation`;
const PLANS_ROOT_PACKAGE = `${BASEAPI}/XtramBillingSubscriptions/plans/package`;
const PLANS_ROOT_MONTHLY = `${BASEAPI}/XtramBillingSubscriptions/plans/monthly`;
const DOCUMENTS_ROOT = `${BASEAPI}/XtramBillingPaymentDocuments`;

const getCurrentMonthKey = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/*──────────────────────── helpers ────────────────────────*/
const getCurrentToken = async () => {
    const raw = localStorage.getItem('token');
    return raw ? JSON.parse(raw) : null;
};

const jsonHeaders = async () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await getCurrentToken()}`
});

const guard = async (res) => {
    if (!res.ok) {
        const txt = await res.text();
        let msg;
        try { msg = JSON.parse(txt).error ?? txt; }
        catch { msg = txt; }
        throw new Error(msg || `HTTP ${res.status}`);
    }
    return res;
};

const authHeaders = async () => ({
    Authorization: `Bearer ${await getCurrentToken()}`
});

/*──────────────────────── accountant API ─────────────────*/
const accountant = {
    /* NEW ➊ ─ fetch masked card */
    getPaymentMethod: async (accountantId) => {
        const res = await fetch(`${ROOT}/accountant/payment-method/${accountantId}`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();   // { last4, expiryMonth, expiryYear }
        return data;
    },
    startTrial: async (dto) => {
        const res = await fetch(`${ROOT}/accountant/trial`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();
    },

    startPaid: async (dto) => {
        const res = await fetch(`${ROOT}/accountant/paid`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();
    },

    /* NEW ➜ save or update card */
    upsertPaymentMethod: async (dto /* UpsertPaymentMethodDto */) => {
        const res = await fetch(`${ROOT}/accountant/payment-method`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();               // { success:true }
    },

    /* NEW ➜ save MASAV (bank-debit) collection details */
    upsertMasavMethod: async (dto /* UpsertMasavMethodDto */) => {
        const res = await fetch(`${ROOT}/accountant/masav-method`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();               // { success:true }
    },

    /* NEW ➜ upload MASAV bank-credential document */
    uploadMasavDocument: async (userId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('userId', userId);
        const res = await fetch(`${ROOT}/accountant/masav-document`, {
            method: 'POST', headers: await authHeaders(), body: fd
        }).then(guard);
        return res.json();               // { success:true, data:{ key, url } }
    },

    cancel: async (accountantId) => {
        const res = await fetch(`${ROOT}/accountant/cancel/${accountantId}`, {
            method: 'POST', headers: await jsonHeaders()
        }).then(guard);
        return res.json();
    },

    status: async (accountantId) => {
        const res = await fetch(`${ROOT}/accountant/status/${accountantId}`, {
            method: 'GET', headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();
        return data;                      // PlanStatusAccountantDto
    },

    preview: async (accountantId, yearly = false) => {
        const res = await fetch(
            `${ROOT}/accountant/preview/${accountantId}?yearly=${yearly}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { amount } = await res.json();
        return amount;                    // float (₪)
    },

    paymentHistory: async (accountantId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/accountant/payment-history/${accountantId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // PaymentHistoryResponseDto
    },

    previewUnpaidMonths: async (caseId) => {
        const res = await fetch(
            `${ROOT}/client/unpaid-months/preview/${caseId}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        return res.json();                // { success, amount, data: ClientArrangementCalculationResponse }
    },

    /** Consolidated: pay client balance, create OTCs, change payer to accountant, close plan, unblock. */
    moveClientToAccountant: async (dto) => {
        const res = await fetch(
            `${ROOT}/client/move-to-accountant`,
            {
                method: 'POST',
                headers: await jsonHeaders(),
                body: JSON.stringify(dto)
            }
        ).then(guard);
        return res.json();
    },

    /** @deprecated Use moveClientToAccountant instead */
    payUnpaidMonths: async (dto) => {
        const res = await fetch(
            `${ROOT}/client/unpaid-months/pay`,
            {
                method: 'POST',
                headers: await jsonHeaders(),
                body: JSON.stringify(dto)
            }
        ).then(guard);
        return res.json();
    },

    /** @deprecated Use moveClientToAccountant instead */
    moveFutureMonthsToAccountant: async (dto) => {
        const res = await fetch(
            `${ROOT}/client/unpaid-months/move-to-accountant`,
            {
                method: 'POST',
                headers: await jsonHeaders(),
                body: JSON.stringify(dto)
            }
        ).then(guard);
        return res.json();
    },

    salesHistory: async (accountantId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/accountant/sales-history/${accountantId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // SalesHistoryResponseDto
    },

    refundHistory: async (accountantId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/accountant/refund-history/${accountantId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // RefundHistoryResponseDto
    },

    /* NEW: resolve plan meta */
    getPackagePlan: async (id) => {
        const res = await fetch(`${PLANS_ROOT_PACKAGE}/${id}`, { headers: await jsonHeaders() }).then(guard);
        const { data } = await res.json();
        return data; // { id, packageName, ... }
    },
    getMonthlyPlan: async (id) => {
        const res = await fetch(`${PLANS_ROOT_MONTHLY}/${id}`, { headers: await jsonHeaders() }).then(guard);
        const { data } = await res.json();
        return data; // { id, name, amount, ... }
    },

    /** Get arrangement (cycles, payments, refunds, discounts, OTC) for this accountant. Server verifies accountantId. */
    getArrangement: async (accountantId) => {
        const res = await fetch(`${ARRANGEMENT_ROOT}/by-accountant/${encodeURIComponent(accountantId)}`, {
            headers: await jsonHeaders()
        }).then(guard);
        return res.json();
    },

    /** Invoices / credit notes linked to this accountant's payments and refunds. */
    getPaymentDocuments: async (accountantId) => {
        const res = await fetch(`${DOCUMENTS_ROOT}/accountant/${encodeURIComponent(accountantId)}`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();
        return data || [];
    },

    /** Short-lived link to one invoice / credit note. */
    getPaymentDocumentUrl: async (accountantId, documentId) => {
        const res = await fetch(`${DOCUMENTS_ROOT}/accountant/${encodeURIComponent(accountantId)}/${documentId}/url`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { url } = await res.json();
        return url;
    },

    /** Monthly breakdown + totals (same payload as xtram manager OverviewTab). fromMonthKey optional. */
    getCalculation: async (accountantId, fromMonthKey, toMonthKey) => {
        let url = `${ACC_CALC_ROOT}/${encodeURIComponent(accountantId)}?toMonthKey=${encodeURIComponent(toMonthKey)}`;
        if (fromMonthKey) url += `&fromMonthKey=${encodeURIComponent(fromMonthKey)}`;
        const res = await fetch(url, { headers: await jsonHeaders() }).then(guard);
        return res.json();
    },

    /** Download billing report PDF: from=null (server uses earliest plan start), to=current month. */
    exportPdf: async (accountantId) => {
        const to = getCurrentMonthKey();
        const url = `${EXPORT_ROOT}/accountant/${encodeURIComponent(accountantId)}/pdf?toMonthKey=${encodeURIComponent(to)}`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    },

    /** Download billing report Excel: from=null, to=current month. */
    exportExcel: async (accountantId) => {
        const to = getCurrentMonthKey();
        const url = `${EXPORT_ROOT}/accountant/${encodeURIComponent(accountantId)}/excel?toMonthKey=${encodeURIComponent(to)}`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    },

    /** PDF of office clients split by self-paid / accountant-paid (card / unpaid status). */
    exportClientsPdf: async (accountantId) => {
        const url = `${EXPORT_ROOT}/accountant/${encodeURIComponent(accountantId)}/clients/pdf`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    },

    /** Excel of office clients split by self-paid / accountant-paid (card / unpaid status). */
    exportClientsExcel: async (accountantId) => {
        const url = `${EXPORT_ROOT}/accountant/${encodeURIComponent(accountantId)}/clients/excel`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    }
};

/*──────────────────────── client API ─────────────────────*/
const client = {
    getPaymentMethod: async (caseId) => {
        const res = await fetch(`${ROOT}/client/payment-method/${caseId}`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();   // { last4, expiryMonth, expiryYear }
        return data;
    },

    startTrial: async (dto) => {
        const res = await fetch(`${ROOT}/client/trial`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();
    },

    startPaid: async (dto) => {
        const res = await fetch(`${ROOT}/client/paid`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();
    },

    /* NEW ➜ save or update card */
    upsertPaymentMethod: async (dto /* UpsertPaymentMethodDto */) => {
        const res = await fetch(`${ROOT}/client/payment-method`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();               // { success:true }
    },

    /* NEW ➜ save MASAV (bank-debit) collection details */
    upsertMasavMethod: async (dto /* UpsertMasavMethodDto */) => {
        const res = await fetch(`${ROOT}/client/masav-method`, {
            method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(dto)
        }).then(guard);
        return res.json();               // { success:true }
    },

    /* NEW ➜ upload MASAV bank-credential document */
    uploadMasavDocument: async (userId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('userId', userId);
        const res = await fetch(`${ROOT}/client/masav-document`, {
            method: 'POST', headers: await authHeaders(), body: fd
        }).then(guard);
        return res.json();               // { success:true, data:{ key, url } }
    },

    cancel: async (caseId) => {
        const res = await fetch(`${ROOT}/client/cancel/${caseId}`, {
            method: 'POST', headers: await jsonHeaders()
        }).then(guard);
        return res.json();
    },

    status: async (caseId) => {
        const res = await fetch(`${ROOT}/client/status/${caseId}`, {
            method: 'GET', headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();
        return data;                      // PlanStatusClientDto
    },

    statusBatch: async (caseIds) => {
        const res = await fetch(`${ROOT}/client/status/batch`, {
            method: 'POST', 
            headers: await jsonHeaders(), 
            body: JSON.stringify(caseIds)
        }).then(guard);
        const { data } = await res.json();
        return data;                      // Dictionary<string, PlanStatusClientDto>
    },

    preview: async (caseId, yearly = false) => {
        const res = await fetch(
            `${ROOT}/client/preview/${caseId}?yearly=${yearly}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { amount } = await res.json();
        return amount;                    // float (₪)
    },

    switchToBusiness: async (dto) => {
        const res = await fetch(`${ROOT}/client/switch-to-business`, {
            method: 'POST',
            headers: await jsonHeaders(),
            body: JSON.stringify(dto)
        }).then(guard);
        return res.json();        // { success:true }
    },

    paymentHistory: async (caseId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/client/payment-history/${caseId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // PaymentHistoryResponseDto
    },

    salesHistory: async (caseId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/client/sales-history/${caseId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // SalesHistoryResponseDto
    },

    refundHistory: async (caseId, pageNumber = 1, pageSize = 1000) => {
        const res = await fetch(
            `${ROOT}/client/refund-history/${caseId}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
            { headers: await jsonHeaders() }
        ).then(guard);
        const { data } = await res.json();
        return data;                      // RefundHistoryResponseDto
    },

    /** Get arrangement (cycles, payments, refunds, discounts, OTC) for this client. Server verifies caseId. */
    getArrangement: async (caseId) => {
        const res = await fetch(`${ARRANGEMENT_ROOT}/by-client/${encodeURIComponent(caseId)}`, {
            headers: await jsonHeaders()
        }).then(guard);
        return res.json();
    },

    /** Invoices / credit notes linked to this client's payments and refunds. */
    getPaymentDocuments: async (caseId) => {
        const res = await fetch(`${DOCUMENTS_ROOT}/client/${encodeURIComponent(caseId)}`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { data } = await res.json();
        return data || [];
    },

    /** Short-lived link to one invoice / credit note. */
    getPaymentDocumentUrl: async (caseId, documentId) => {
        const res = await fetch(`${DOCUMENTS_ROOT}/client/${encodeURIComponent(caseId)}/${documentId}/url`, {
            headers: await jsonHeaders()
        }).then(guard);
        const { url } = await res.json();
        return url;
    },

    /** Monthly breakdown + totals for client arrangement. fromMonthKey optional. */
    getCalculation: async (caseId, fromMonthKey, toMonthKey) => {
        let url = `${CLI_CALC_ROOT}/${encodeURIComponent(caseId)}?toMonthKey=${encodeURIComponent(toMonthKey)}`;
        if (fromMonthKey) url += `&fromMonthKey=${encodeURIComponent(fromMonthKey)}`;
        const res = await fetch(url, { headers: await jsonHeaders() }).then(guard);
        return res.json();
    },

    /** Download billing report PDF: from=null (server uses earliest plan start), to=current month. */
    exportPdf: async (caseId) => {
        const to = getCurrentMonthKey();
        const url = `${EXPORT_ROOT}/client/${encodeURIComponent(caseId)}/pdf?toMonthKey=${encodeURIComponent(to)}`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    },

    /** Download billing report Excel: from=null, to=current month. */
    exportExcel: async (caseId) => {
        const to = getCurrentMonthKey();
        const url = `${EXPORT_ROOT}/client/${encodeURIComponent(caseId)}/excel?toMonthKey=${encodeURIComponent(to)}`;
        const res = await fetch(url, { headers: await authHeaders() }).then(guard);
        return res.blob();
    }
};

/*──────────────────────── export default ────────────────*/
const PaymentService = { accountant, client };
export default PaymentService;
